import { useQuery, useMutation } from '@tanstack/react-query';
import { getLandingPage, setLandingPage } from '@api/landingPage';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';

const DEFAULT_LANDING_PAGE_QUERY_KEY = 'defaultLandingPage';

/**
 * Hook for initial loading of default landing page data
 * Should only be used once during app initialization
 */
export const useInitialDefaultLandingPage = () => {
  const updateDefaultLandingPage = useDefaultLandingPageStore(
    (state) => state.updateDefaultLandingPage,
  );
  const customerId = useDefaultLandingPageStore((state) => state.customerId);

  const query = useQuery({
    queryKey: [DEFAULT_LANDING_PAGE_QUERY_KEY, customerId],
    queryFn: async () => {
      const resp = await getLandingPage({}, {});
      const { data } = resp;
      // Update store with configuration and isFromCustomerDefault flag
      updateDefaultLandingPage({
        ...data.configuration,
        isFromCustomerDefault: data.isFromCustomerDefault || false,
      });
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity, // Prevent automatic refetching
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

/**
 * Hook for mutating default landing page
 * Only updates the backend and local store, no fetching
 */
export const useDefaultLandingPageMutation = () => {
  const updateDefaultLandingPage = useDefaultLandingPageStore(
    (state) => state.updateDefaultLandingPage,
  );

  const mutation = useMutation({
    mutationFn: async (payload: { url: string; name: string; path: string }) => {
      const response = await setLandingPage(payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update local store with personal default (not customer default)
      updateDefaultLandingPage({
        ...variables,
        isFromCustomerDefault: false,
      });
      return data;
    },
  });

  return {
    setDefaultLandingPage: mutation.mutate,
    setDefaultLandingPageAsync: mutation.mutateAsync,
    isUpdating: mutation.isLoading,
    updateError: mutation.error,
  };
};

export default useDefaultLandingPageMutation;
